/**
 * Who hears what (sched-flow.md 4.4): per device, a user picks the rooms and
 * the signal kinds that ping this screen, with a chime. Escalations, urgent
 * and emergency pages always come through. The person who raised a signal is
 * never pinged for it (SF-05), but hears when someone claims it.
 *
 * Subscriptions are a per-device convenience, so they live in this browser.
 */
import { SIGNAL_KINDS, type Signal, type SignalKind } from '@velora/contracts/lightboard';

export interface AlertPrefs {
  /** `all` or the operatory ids this device follows. */
  rooms: 'all' | string[];
  kinds: SignalKind[];
  chime: boolean;
}

export const ALWAYS_KINDS: readonly SignalKind[] = ['urgent', 'emergency'];

export const DEFAULT_ALERTS: AlertPrefs = {
  rooms: 'all',
  kinds: SIGNAL_KINDS.filter((k) => k !== 'xray' && k !== 'billing'),
  chime: true,
};

const KEY = 'velora.lightboard.alerts';

export function loadAlerts(): AlertPrefs {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    if (!raw) return DEFAULT_ALERTS;
    const v = JSON.parse(raw) as Partial<AlertPrefs>;
    return {
      rooms: v.rooms === 'all' || Array.isArray(v.rooms) ? v.rooms : 'all',
      kinds: Array.isArray(v.kinds)
        ? v.kinds.filter((k): k is SignalKind => SIGNAL_KINDS.includes(k))
        : DEFAULT_ALERTS.kinds,
      chime: typeof v.chime === 'boolean' ? v.chime : true,
    };
  } catch {
    return DEFAULT_ALERTS;
  }
}

export function saveAlerts(prefs: AlertPrefs): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Private window: the choice lasts for this tab.
  }
}

export type AlertReason = 'new' | 'escalated' | 'claimed';

export interface Alert {
  signal: Signal;
  reason: AlertReason;
}

function follows(prefs: AlertPrefs, s: Signal): boolean {
  if (ALWAYS_KINDS.includes(s.kind)) return true;
  if (!prefs.kinds.includes(s.kind)) return false;
  if (prefs.rooms === 'all' || s.operatoryId === null) return true;
  return prefs.rooms.includes(s.operatoryId);
}

/**
 * Alerts between two board snapshots. The first snapshot after opening the
 * board (`prev === null`) alerts nothing, so a reload never replays old pings.
 */
export function diffAlerts(
  prev: readonly Signal[] | null,
  next: readonly Signal[],
  me: string,
  prefs: AlertPrefs,
): Alert[] {
  if (prev === null) return [];
  const before = new Map(prev.map((s) => [s.id, s]));
  const out: Alert[] = [];
  for (const s of next) {
    const was = before.get(s.id);
    if (!was) {
      if (s.raisedBy.id !== me && follows(prefs, s)) out.push({ signal: s, reason: 'new' });
      continue;
    }
    if (s.escalated && !was.escalated && s.raisedBy.id !== me) {
      out.push({ signal: s, reason: 'escalated' });
      continue;
    }
    if (s.claimedBy && !was.claimedBy && s.raisedBy.id === me && s.claimedBy.id !== me) {
      out.push({ signal: s, reason: 'claimed' });
    }
  }
  return out;
}

/* ------------------------------------------------------------------------ */
/* Chime                                                                     */
/* ------------------------------------------------------------------------ */

let audio: AudioContext | null = null;

/**
 * A short two-note chime (three for emergencies), synthesised so no audio
 * file ships. Browsers allow sound only after a click on the page; until
 * then this does nothing.
 */
export function chime(kind: 'normal' | 'urgent'): void {
  try {
    const Ctx = globalThis.AudioContext;
    if (!Ctx) return;
    audio ??= new Ctx();
    if (audio.state === 'suspended') void audio.resume();
    const notes = kind === 'urgent' ? [880, 660, 880] : [784, 1046];
    const t0 = audio.currentTime;
    notes.forEach((freq, i) => {
      if (!audio) return;
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = t0 + i * 0.16;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
      osc.connect(gain).connect(audio.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    });
  } catch {
    // No audio device: the toast still shows.
  }
}
